"use client"

import React, { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight, Package, Tag, Eye, AlertTriangle, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { Badge } from "../../../components/ui/badge"
import { Button } from "../../../components/ui/button"

export default function InventoryTable({ items, onItemClick, categories, isLoading, clientId, searchTerm, customColumns = [] }) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' })
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // User-friendly columns config
  const FIXED_COLUMNS = [
    { key: 'name', label: 'Nombre' },
    { key: 'sku', label: 'SKU' },
    { key: 'category_name', label: 'Categoría' },
    { key: 'subcategory_name', label: 'Subcategoría' },
    { key: 'stock', label: 'Stock' },
    { key: 'unit', label: 'Unidad' },
    { key: 'common_price', label: 'Precio' },
    { key: 'description', label: 'Descripción' },
  ]

  // Sort function
  const handleSort = (field) => {
    let direction = 'asc'
    if (sortConfig.key === field && sortConfig.direction === 'asc') {
      direction = 'desc'
    }
    setSortConfig({ key: field, direction })
  }

  // Sort items
  const sortedItems = useMemo(() => {
    let sortableItems = [...items]
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        const aValue = a[sortConfig.key] || ''
        const bValue = b[sortConfig.key] || ''
        
        if (typeof aValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue)
        }
        
        if (sortConfig.direction === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
      })
    }
    return sortableItems
  }, [items, sortConfig])

  // Pagination
  const totalItems = sortedItems.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems)
  const paginatedItems = sortedItems.slice(startIndex, endIndex)

  // Pagination controls
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
  }

  const goToFirstPage = () => goToPage(1)
  const goToLastPage = () => goToPage(totalPages)
  const goToPreviousPage = () => goToPage(currentPage - 1)
  const goToNextPage = () => goToPage(currentPage + 1)

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const delta = 2
    const range = []
    const rangeWithDots = []

    for (let i = Math.max(2, currentPage - delta); 
         i <= Math.min(totalPages - 1, currentPage + delta); 
         i++) {
      range.push(i)
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...')
    } else {
      rangeWithDots.push(1)
    }

    rangeWithDots.push(...range)

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages)
    } else {
      rangeWithDots.push(totalPages)
    }

    return rangeWithDots
  }

  // Get category name helper
  const getCategoryName = (categoryId) => {
    const category = categories?.find(c => c.id === categoryId)
    return category?.name || 'Sin categoría'
  }

  // Get subcategory name helper
  const getSubcategoryName = (categoryId, subcategoryId) => {
    const category = categories?.find(c => c.id === categoryId)
    const subcategory = category?.subcategories?.find(s => s.id === subcategoryId)
    return subcategory?.name || 'Sin subcategoría'
  }

  // Get stock status
  const getStockStatus = (item) => {
    if (!item.current_stock || !item.min_stock_threshold) {
      return { label: "Desconocido", color: "bg-slate-200 text-slate-800" }
    }
    
    if (item.current_stock <= 0) {
      return { label: "Sin Stock", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" }
    }
    
    if (item.current_stock <= item.min_stock_threshold) {
      return { label: "Poco Stock", color: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" }
    }
    
    return { label: "En Stock", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" }
  }

  if (isLoading) {
  return (
      <div className="space-y-4">
        {/* Loading Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Cargando elementos de inventario...</span>
          </div>
        </div>
        
        {/* Loading Skeleton */}
        <div className="space-y-3 p-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center space-x-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded animate-pulse">
              <div className="w-8 h-8 bg-slate-200 dark:bg-slate-700 rounded"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4"></div>
                <div className="h-3 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
              </div>
              <div className="w-20 h-6 bg-slate-200 dark:bg-slate-700 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden">
      {/* Clean UI Table Header */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-700">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
              Mostrando <span className="font-medium text-slate-900 dark:text-slate-100">{startIndex + 1}</span> a{' '}
              <span className="font-medium text-slate-900 dark:text-slate-100">{endIndex}</span> de{' '}
              <span className="font-medium text-slate-900 dark:text-slate-100">{totalItems}</span> elementos
            </div>
                </div>
          
          <div className="flex items-center gap-3">
            <label className="text-sm text-slate-600 dark:text-slate-400">Mostrar:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="px-3 py-1 text-sm border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
              <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
            <span className="text-sm text-slate-600 dark:text-slate-400">por página</span>
          </div>
        </div>
      </div>

      {/* Clean UI Table Content */}
      <div className="overflow-x-auto">
        {paginatedItems.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
              {totalItems === 0 ? 'No se encontraron elementos de inventario' : 'No hay elementos en esta página'}
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              {totalItems === 0 
                ? 'Comienza creando tu primer elemento de inventario'
                : 'Intenta cambiar los filtros o navegar a otra página'
              }
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/50">
                    {FIXED_COLUMNS.map((col) => (
                      <th 
                        key={col.key}
                        className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                        onClick={() => handleSort(col.key)}
                      >
                        <div className="flex items-center space-x-1">
                          <span>{col.label}</span>
                          {sortConfig.key === col.key && (
                            <span className="text-orange-500">
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Acciones</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                  {paginatedItems.map((item, index) => (
                    <tr 
                      key={item.id || index} 
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                      onClick={() => onItemClick(item)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <Package className="h-4 w-4 text-slate-400 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              {item.name || "Elemento Sin Nombre"}
                            </div>
                            <div className="text-sm text-slate-500 dark:text-slate-400">
                              ID: {item.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant="outline" className="rounded border-slate-300 dark:border-slate-600">
                          <Tag className="h-3 w-3 mr-1" />
                          {item.sku || "Sin SKU"}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900 dark:text-slate-100">
                        {getCategoryName(item.category_id)}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                        {getSubcategoryName(item.category_id, item.subcategory_id)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {item.current_stock || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                        {item.unit || 'Unidad'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          ${item.common_price || '0.00'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-slate-500 dark:text-slate-400 truncate max-w-xs">
                          {item.description || 'Sin descripción'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {(() => {
                          const status = getStockStatus(item)
                          return (
                            <Badge className={`${status.color} rounded`}>
                              {status.label === "Sin Stock" && <XCircle className="h-3 w-3 mr-1" />}
                              {status.label === "Poco Stock" && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {status.label === "En Stock" && <CheckCircle2 className="h-3 w-3 mr-1" />}
                              {status.label}
                            </Badge>
                          )
                        })()}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded"
                          onClick={(e) => {
                            e.stopPropagation()
                            onItemClick(item)
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-4 p-4">
              {paginatedItems.map((item, index) => {
                const status = getStockStatus(item)
                return (
                  <div 
                    key={item.id || index}
                    className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => onItemClick(item)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center">
                        <Package className="h-5 w-5 text-slate-400 mr-3" />
                        <div>
                          <h3 className="font-medium text-slate-900 dark:text-slate-100">
                            {item.name || "Elemento Sin Nombre"}
                          </h3>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {item.sku || "Sin SKU"}
                          </p>
                        </div>
                      </div>
                      <Badge className={`${status.color} rounded`}>
                        {status.label}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-slate-500 dark:text-slate-400">Categoría:</span>
                        <p className="font-medium text-slate-900 dark:text-slate-100">
                          {getCategoryName(item.category_id)}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500 dark:text-slate-400">Stock:</span>
                        <p className="font-medium text-slate-900 dark:text-slate-100">
                          {item.current_stock || 0} {item.unit || 'unidades'}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500 dark:text-slate-400">Precio:</span>
                        <p className="font-medium text-slate-900 dark:text-slate-100">
                          ${item.common_price || '0.00'}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-500 dark:text-slate-400">ID:</span>
                        <p className="font-medium text-slate-900 dark:text-slate-100">
                          {item.id}
                        </p>
                </div>
              </div>
              
                    {item.description && (
                      <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
                        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                          {item.description}
                        </p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Clean UI Pagination */}
              {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Página <span className="font-medium text-slate-900 dark:text-slate-100">{currentPage}</span> de{' '}
              <span className="font-medium text-slate-900 dark:text-slate-100">{totalPages}</span>
            </div>
            
            <div className="flex items-center space-x-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToFirstPage}
                    disabled={currentPage === 1}
                className="rounded border-slate-300 dark:border-slate-600"
                  >
                <ChevronLeft className="h-4 w-4" />
                <ChevronLeft className="h-4 w-4 -ml-1" />
                  </Button>
              
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                className="rounded border-slate-300 dark:border-slate-600"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
              <div className="flex items-center space-x-1">
                {totalPages <= 7 ? (
                  Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(page)}
                      className={`rounded ${
                        currentPage === page 
                          ? 'bg-orange-500 text-white hover:bg-orange-600' 
                          : 'border-slate-300 dark:border-slate-600'
                      }`}
                    >
                      {page}
                    </Button>
                  ))
                ) : (
                  getPageNumbers().map((page, index) => (
                    <React.Fragment key={index}>
                      {page === '...' ? (
                        <span className="px-3 py-1 text-slate-500">...</span>
                    ) : (
                      <Button
                          variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                          onClick={() => goToPage(page)}
                          className={`rounded ${
                            currentPage === page 
                              ? 'bg-orange-500 text-white hover:bg-orange-600' 
                              : 'border-slate-300 dark:border-slate-600'
                        }`}
                      >
                          {page}
                      </Button>
                      )}
                    </React.Fragment>
                  ))
                )}
              </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                className="rounded border-slate-300 dark:border-slate-600"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
              
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={goToLastPage}
                    disabled={currentPage === totalPages}
                className="rounded border-slate-300 dark:border-slate-600"
                  >
                <ChevronRight className="h-4 w-4" />
                <ChevronRight className="h-4 w-4 -ml-1" />
                  </Button>
            </div>
              </div>
            </div>
          )}
        </div>
  )
}
